'use client';

import Inventory2Icon from '@mui/icons-material/Inventory2';
import ClearIcon from '@mui/icons-material/Clear';
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link as RouterLink, useParams, useSearchParams } from 'react-router-dom';
import TitleBar from '../layout/TitleBar';
import {
  applyInventoryAuditorSiteVisit,
  getInventoryAuditorImageUploadUrl,
  queryInventoryAuditorBalancesByLocation,
  resolveInventoryAuditorUpc,
  syncInventoryAuditorSiteVisit,
  uploadInventoryAuditorImageToPresignedUrl,
  type InventoryAuditorBalanceRow,
  type InventoryAuditorUpcVariant,
} from '@/hooks/useInventoryAuditorInventory';
import { useInventoryRoomSync, rowIsCleanForRemoteMerge } from '@/hooks/useInventoryRoomSync';
import type { InventoryCountUpdatedEvent } from '@/realtime/inventorySocket';
import { useAuth } from '@/contexts/AuthContext';
import {
  INVENTORY_AUDITOR_SIZES,
  INVENTORY_AUDITOR_UOM_PACK_TYPES,
  inventoryAuditorCatalogOriginLabel,
} from '@/stack-shared/constants/inventory-auditor-catalog.constants';
import { useInventoryAuditorLocationsList } from '@/hooks/useInventoryAuditorLocation';
import { useNotification } from '@/contexts/NotificationContext';
import {
  DEFAULT_COUNT_CALENDAR_TIMEZONE,
  formatBuCalendarDayLabel,
} from '@/utils/buCalendar';

/** Display API count date (`YYYY-MM-DD`) in the business unit calendar zone. */
function formatIaCountDateLabel(ymd: string, timeZone: string = DEFAULT_COUNT_CALENDAR_TIMEZONE): string {
  return formatBuCalendarDayLabel(ymd, timeZone);
}

const rowKey = (inventoryItemId: number) => String(inventoryItemId);
const IA_COUNT_AUTO_SAVE_MS = 750;

const InventoryAuditorLocationSiteVisitPage: React.FC = () => {
  const { locationId: locationIdParam } = useParams();
  const [searchParams] = useSearchParams();
  const pathLoc =
    locationIdParam != null && locationIdParam !== ''
      ? parseInt(String(locationIdParam), 10)
      : NaN;
  const initialLoc = !Number.isNaN(pathLoc) && pathLoc > 0
    ? pathLoc
    : parseInt(String(searchParams.get('locationId') || ''), 10);
  const { data: locations = [], isLoading: locationsLoading } = useInventoryAuditorLocationsList();
  const { showError } = useNotification();
  const { user } = useAuth();
  const currentUserId =
    user?.id != null ? parseInt(String(user.id), 10) : user?.user?.id != null ? parseInt(String(user.user.id), 10) : null;

  const lastSyncAtRef = useRef(new Date(0).toISOString());

  const [selectedLocation, setSelectedLocation] = useState<{ id: number; name?: string } | null>(null);
  const [tableLoading, setTableLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [dataRefreshKey, setDataRefreshKey] = useState(0);

  const [lastSavedQty, setLastSavedQty] = useState<Record<string, number>>({});
  const [draftQty, setDraftQty] = useState<Record<string, number>>({});
  const saveTimersRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  const [manualUpc, setManualUpc] = useState('');
  const [pickUom, setPickUom] = useState<string>(INVENTORY_AUDITOR_UOM_PACK_TYPES[0]);
  const [pickSize, setPickSize] = useState<string>(INVENTORY_AUDITOR_SIZES[0]);
  const [savingItemIds, setSavingItemIds] = useState<Set<number>>(new Set());
  const savingItemIdsRef = useRef(savingItemIds);
  savingItemIdsRef.current = savingItemIds;
  const lastSavedQtyRef = useRef(lastSavedQty);
  lastSavedQtyRef.current = lastSavedQty;
  const draftQtyRef = useRef(draftQty);
  draftQtyRef.current = draftQty;

  const [variantDialogOpen, setVariantDialogOpen] = useState(false);
  const [pendingVariants, setPendingVariants] = useState<InventoryAuditorUpcVariant[]>([]);
  const [removeRowItemId, setRemoveRowItemId] = useState<number | null>(null);
  const [newSkuConfirm, setNewSkuConfirm] = useState<{
    scannedCode: string;
    primaryUpc: string;
    retailUpc: string;
    name: string | null;
    imageUrl: string | null;
    uomPackType: string;
    size: string;
  } | null>(null);
  const [newSkuConfirmSaving, setNewSkuConfirmSaving] = useState(false);
  const [newSkuPhotoFile, setNewSkuPhotoFile] = useState<File | null>(null);
  const [newSkuPhotoPreview, setNewSkuPhotoPreview] = useState<string | null>(null);
  const [newSkuScanContext, setNewSkuScanContext] = useState('');
  const newSkuPhotoInputRef = useRef<HTMLInputElement | null>(null);

  /** Server-provided count calendar day (`YYYY-MM-DD`, see API). */
  const [countDateYmd, setCountDateYmd] = useState<string | null>(null);
  const [countCalendarTimeZone, setCountCalendarTimeZone] = useState(
    () => user?.currentBusinessUnit?.countCalendarTimeZone ?? DEFAULT_COUNT_CALENDAR_TIMEZONE,
  );
  const [dayRolloverBanner, setDayRolloverBanner] = useState(false);
  const prevCountDateRef = useRef<string | null>(null);

  const [rows, setRows] = useState<InventoryAuditorBalanceRow[]>([]);
  const rowsRef = useRef(rows);
  rowsRef.current = rows;
  /** After Look up: scroll/focus this row’s Count field (mobile count-sheet parity). */
  const [focusItemId, setFocusItemId] = useState<number | null>(null);
  const [highlightItemId, setHighlightItemId] = useState<number | null>(null);
  const countInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  const initialFromUrlApplied = useRef(false);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => window.clearTimeout(t);
  }, [search]);

  useEffect(() => {
    if (initialFromUrlApplied.current || locationsLoading || locations.length === 0) return;
    initialFromUrlApplied.current = true;
    if (!Number.isNaN(initialLoc) && initialLoc > 0) {
      const m = locations.find((l: { id: number }) => l.id === initialLoc);
      if (m) {
        setSelectedLocation({ id: m.id, name: m.name });
      }
    }
  }, [locationsLoading, locations, initialLoc]);

  useEffect(() => {
    if (!selectedLocation) {
      setRows([]);
      setLastSavedQty({});
      setDraftQty({});
      setCountDateYmd(null);
      return;
    }
    let cancelled = false;
    setTableLoading(true);
    void (async () => {
      try {
        const r = await queryInventoryAuditorBalancesByLocation({
          locationId: selectedLocation.id,
          skip: 0,
          take: 8000,
          search: debouncedSearch,
          orderBy: 'upc',
          orderDirection: 'ASC',
        });
        if (cancelled) return;
        const prevDate = prevCountDateRef.current;
        if (prevDate && r.countDate && prevDate !== r.countDate) {
          setDayRolloverBanner(true);
          setLastSavedQty({});
          setDraftQty({});
        }
        prevCountDateRef.current = r.countDate ?? null;
        // Keep local-only Look up rows (balanceId 0 / qty 0) — server never returns them until counted.
        setRows((prev) => {
          const serverIds = new Set(r.data.map((row) => row.inventoryItem.id));
          const synthetics = prev.filter(
            (row) => row.balanceId === 0 && !serverIds.has(row.inventoryItem.id),
          );
          return [...synthetics, ...r.data];
        });
        setCountDateYmd(r.countDate ?? null);
        if (r.countCalendarTimeZone) {
          setCountCalendarTimeZone(r.countCalendarTimeZone);
        }
        setLastSavedQty((prev) => {
          const next = { ...prev };
          for (const row of r.data) {
            const k = rowKey(row.inventoryItem.id);
            if (savingItemIdsRef.current.has(row.inventoryItem.id)) continue;
            next[k] = row.quantity;
          }
          return next;
        });
        setDraftQty((prev) => {
          const next = { ...prev };
          for (const row of r.data) {
            const k = rowKey(row.inventoryItem.id);
            if (next[k] === undefined) next[k] = row.quantity;
          }
          return next;
        });
      } catch (e: unknown) {
        if (!cancelled) showError(e instanceof Error ? e.message : 'Failed to load balances');
        if (!cancelled) setRows([]);
      } finally {
        if (!cancelled) setTableLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedLocation?.id, debouncedSearch, dataRefreshKey, showError]);

  const saveCount = useCallback(
    async (inventoryItemId: number, quantity?: number, force = false) => {
      if (!selectedLocation) return;
      const k = rowKey(inventoryItemId);
      const qty = quantity ?? draftQty[k] ?? lastSavedQty[k] ?? 0;
      const row = rows.find((r) => r.inventoryItem.id === inventoryItemId);
      if (!force && row?.balanceId === 0 && qty === 0) return;
      if (!force && qty === lastSavedQty[k]) return;

      setSavingItemIds((prev) => new Set(prev).add(inventoryItemId));
      try {
        const { countDate } = await applyInventoryAuditorSiteVisit({
          locationId: selectedLocation.id,
          lines: [{ inventoryItemId, quantity: qty }],
        });
        setCountDateYmd(countDate);
        setLastSavedQty((prev) => ({ ...prev, [k]: qty }));
        setDraftQty((prev) => ({ ...prev, [k]: qty }));
        if (qty === 0) {
          setRows((prev) => prev.filter((r) => r.inventoryItem.id !== inventoryItemId));
          setLastSavedQty((prev) => {
            const n = { ...prev };
            delete n[k];
            return n;
          });
          setDraftQty((prev) => {
            const n = { ...prev };
            delete n[k];
            return n;
          });
        } else {
          setRows((prev) =>
            prev.map((r) => (r.inventoryItem.id === inventoryItemId ? { ...r, quantity: qty } : r)),
          );
        }
      } catch (e: unknown) {
        showError(e instanceof Error ? e.message : 'Save failed');
      } finally {
        setSavingItemIds((prev) => {
          const n = new Set(prev);
          n.delete(inventoryItemId);
          return n;
        });
      }
    },
    [selectedLocation, draftQty, lastSavedQty, rows, showError],
  );

  const scheduleSave = useCallback(
    (inventoryItemId: number) => {
      const k = rowKey(inventoryItemId);
      const existing = saveTimersRef.current[k];
      if (existing) window.clearTimeout(existing);
      saveTimersRef.current[k] = window.setTimeout(() => {
        delete saveTimersRef.current[k];
        void saveCount(inventoryItemId);
      }, IA_COUNT_AUTO_SAVE_MS);
    },
    [saveCount],
  );

  const changeDraft = useCallback(
    (inventoryItemId: number, raw: string, immediateSave = false) => {
      const n = parseInt(raw.replace(/[^\d]/g, '') || '0', 10);
      const q = Number.isNaN(n) || n < 0 ? 0 : n;
      const k = rowKey(inventoryItemId);
      setDraftQty((prev) => ({ ...prev, [k]: q }));
      if (immediateSave) {
        void saveCount(inventoryItemId, q);
      } else {
        scheduleSave(inventoryItemId);
      }
    },
    [saveCount, scheduleSave],
  );

  const handleConfirmRemoveCountRow = useCallback(() => {
    if (removeRowItemId == null) return;
    const id = removeRowItemId;
    const row = rows.find((r) => r.inventoryItem.id === id);
    setRemoveRowItemId(null);
    if (!row) return;
    const k = rowKey(id);
    const saved = lastSavedQty[k] ?? row.quantity;
    const draft = draftQty[k] ?? saved;
    const hadNonZeroCount = saved > 0 || draft > 0;
    if (row.balanceId === 0 && !hadNonZeroCount) {
      setRows((prev) => prev.filter((r) => r.inventoryItem.id !== id));
      setLastSavedQty((prev) => {
        const n = { ...prev };
        delete n[k];
        return n;
      });
      setDraftQty((prev) => {
        const n = { ...prev };
        delete n[k];
        return n;
      });
      return;
    }
    if (row.balanceId > 0 && !hadNonZeroCount) {
      setRows((prev) => prev.filter((r) => r.inventoryItem.id !== id));
      return;
    }
    void saveCount(id, 0, true);
  }, [removeRowItemId, rows, lastSavedQty, draftQty, saveCount]);

  const applyRemoteIaUpdate = useCallback((inventoryItemId: number, quantity: number, updatedAt: string) => {
    const k = rowKey(inventoryItemId);
    if (savingItemIdsRef.current.has(inventoryItemId)) return;
    if (saveTimersRef.current[k]) return;
    const saved = lastSavedQtyRef.current[k];
    const draft = draftQtyRef.current[k] ?? saved ?? quantity;
    if (!rowIsCleanForRemoteMerge(draft, saved)) return;

    if (updatedAt > lastSyncAtRef.current) {
      lastSyncAtRef.current = updatedAt;
    }

    const existsLocally = rowsRef.current.some((r) => r.inventoryItem.id === inventoryItemId);
    if (quantity > 0 && !existsLocally) {
      // Another device added a SKU we never loaded — refetch sparse today’s list.
      setDataRefreshKey((n) => n + 1);
      return;
    }

    setLastSavedQty((prev) => ({ ...prev, [k]: quantity }));
    setDraftQty((prev) => ({ ...prev, [k]: quantity }));
    if (quantity === 0) {
      setRows((prev) => prev.filter((r) => r.inventoryItem.id !== inventoryItemId));
      setLastSavedQty((prev) => {
        const n = { ...prev };
        delete n[k];
        return n;
      });
      setDraftQty((prev) => {
        const n = { ...prev };
        delete n[k];
        return n;
      });
    } else {
      setRows((prev) => {
        const idx = prev.findIndex((r) => r.inventoryItem.id === inventoryItemId);
        if (idx >= 0) {
          const next = [...prev];
          next[idx] = { ...next[idx], quantity };
          return next;
        }
        return prev;
      });
    }
  }, []);

  const handleRemoteIaEvent = useCallback(
    (event: InventoryCountUpdatedEvent) => {
      if (event.domain !== 'ia') return;
      if (event.quantity == null) return;
      applyRemoteIaUpdate(event.inventoryItemId, event.quantity, event.updatedAt);
    },
    [applyRemoteIaUpdate],
  );

  const pollIaSync = useCallback(async () => {
    if (!selectedLocation) return;
    const since = lastSyncAtRef.current;
    const r = await syncInventoryAuditorSiteVisit({
      locationId: selectedLocation.id,
      since,
      countDate: countDateYmd ?? undefined,
    });
    if (r.countDate) setCountDateYmd(r.countDate);
    for (const line of r.lines) {
      applyRemoteIaUpdate(line.inventoryItemId, line.quantity, line.updatedAt);
    }
    const maxAt = r.lines.reduce((m, l) => (l.updatedAt > m ? l.updatedAt : m), since);
    if (maxAt > since) {
      lastSyncAtRef.current = maxAt;
    }
  }, [selectedLocation, countDateYmd, applyRemoteIaUpdate]);

  useInventoryRoomSync({
    enabled: selectedLocation != null,
    join: selectedLocation ? { scope: 'ia_site_visit', locationId: selectedLocation.id } : null,
    currentUserId,
    onRemoteUpdate: handleRemoteIaEvent,
    poll: pollIaSync,
  });

  useEffect(() => {
    if (focusItemId == null || tableLoading) return;
    const id = focusItemId;
    const t = window.setTimeout(() => {
      const el = countInputRefs.current[rowKey(id)];
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        el.focus();
        el.select();
      } else {
        const rowEl = document.querySelector(`[data-inventory-item-id="${id}"]`);
        rowEl?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
      setFocusItemId(null);
    }, 0);
    return () => window.clearTimeout(t);
  }, [focusItemId, rows, tableLoading]);

  useEffect(() => {
    if (highlightItemId == null) return;
    const t = window.setTimeout(() => setHighlightItemId(null), 2000);
    return () => window.clearTimeout(t);
  }, [highlightItemId]);

  const mergeResolvedItemIntoGrid = useCallback(
    (
      iid: number,
      upcVal: string,
      name: string | null,
      imageUrl: string | null,
      uomVal: string,
      sizeVal: string,
      catalogOrigin?: 'pdi' | 'scanned' | null,
      scanContext?: string | null,
    ) => {
      const k = rowKey(iid);
      setRows((prev) => {
        if (prev.some((r) => r.inventoryItem.id === iid)) return prev;
        const synthetic: InventoryAuditorBalanceRow = {
          balanceId: 0,
          quantity: 0,
          inventoryItem: {
            id: iid,
            upc: upcVal,
            name,
            imageUrl,
            uomPackType: uomVal,
            size: sizeVal,
            catalogOrigin: catalogOrigin ?? 'scanned',
            scanContext: scanContext ?? null,
          },
        };
        return [synthetic, ...prev];
      });
      setLastSavedQty((prev) => ({ ...prev, [k]: prev[k] ?? 0 }));
      setDraftQty((prev) => ({ ...prev, [k]: prev[k] ?? 0 }));
      setManualUpc('');
      setFocusItemId(iid);
      setHighlightItemId(iid);
    },
    [],
  );

  const resetNewSkuConfirmForm = useCallback(() => {
    setNewSkuConfirm(null);
    setNewSkuPhotoFile(null);
    if (newSkuPhotoPreview) {
      URL.revokeObjectURL(newSkuPhotoPreview);
    }
    setNewSkuPhotoPreview(null);
    setNewSkuScanContext('');
  }, [newSkuPhotoPreview]);

  const handlePickVariant = useCallback(
    (v: InventoryAuditorUpcVariant) => {
      setVariantDialogOpen(false);
      setPendingVariants([]);
      mergeResolvedItemIntoGrid(
        v.inventoryItemId,
        v.retailUpc ?? v.primaryUpc,
        v.name,
        v.imageUrl,
        v.uomPackType,
        v.size,
        v.catalogOrigin,
        null,
      );
    },
    [mergeResolvedItemIntoGrid],
  );

  const handleAddUpc = useCallback(async () => {
    if (!selectedLocation) {
      showError('Pick a location first');
      return;
    }
    const code = manualUpc.trim();
    if (!code) return;
    try {
      const res = await resolveInventoryAuditorUpc(code, {
        autoCreateInCatalog: false,
        uomPackType: pickUom,
        size: pickSize,
      });

      if (res.requiresDisambiguation === true && res.variants != null && res.variants.length > 0) {
        setPendingVariants(res.variants);
        setVariantDialogOpen(true);
        return;
      }

      if (res.requiresCatalogAttributes) {
        showError('Choose UOM and Size, then try again');
        return;
      }

      if (res.notInCatalog === true) {
        setNewSkuPhotoFile(null);
        setNewSkuPhotoPreview(null);
        setNewSkuScanContext('');
        const previewName = res.name?.trim() ?? '';
        setNewSkuConfirm({
          scannedCode: code,
          primaryUpc: res.primaryUpc ?? code,
          retailUpc: res.retailUpc ?? res.primaryUpc ?? code,
          name: /^scanned product\b/i.test(previewName) ? null : res.name ?? null,
          imageUrl: res.imageUrl ?? null,
          uomPackType: res.uomPackType ?? pickUom,
          size: res.size ?? pickSize,
        });
        return;
      }

      if (!res.found || res.inventoryItemId == null) {
        showError('Could not resolve this product code');
        return;
      }

      // Incomplete scanned placeholders (no photo/notes) — require confirm instead of silent add.
      const incompleteScanned =
        res.catalogOrigin === 'scanned' &&
        (!(res.scanContext != null && String(res.scanContext).trim() !== '') ||
          !(res.imageS3Key != null && String(res.imageS3Key).trim() !== ''));
      if (incompleteScanned) {
        setNewSkuPhotoFile(null);
        setNewSkuPhotoPreview(null);
        setNewSkuScanContext('');
        const previewName = res.name?.trim() ?? '';
        setNewSkuConfirm({
          scannedCode: code,
          primaryUpc: res.primaryUpc ?? code,
          retailUpc: res.retailUpc ?? res.primaryUpc ?? code,
          name: /^scanned product\b/i.test(previewName) ? null : res.name ?? null,
          imageUrl: res.imageUrl ?? null,
          uomPackType: res.uomPackType ?? pickUom,
          size: res.size ?? pickSize,
        });
        return;
      }

      if (res.catalogIncomplete) {
        showError('This SKU is missing UOM/Size in the catalog. Ask an admin to update the item.');
      }
      mergeResolvedItemIntoGrid(
        res.inventoryItemId,
        res.retailUpc ?? res.primaryUpc ?? code,
        res.name ?? null,
        res.imageUrl ?? null,
        res.uomPackType ?? pickUom,
        res.size ?? pickSize,
        res.catalogOrigin,
        res.scanContext ?? null,
      );
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : 'Lookup failed');
    }
  }, [selectedLocation, manualUpc, pickUom, pickSize, showError, mergeResolvedItemIntoGrid]);

  const handleConfirmNewScannedSku = useCallback(async () => {
    if (!newSkuConfirm) return;
    const context = newSkuScanContext.trim();
    if (!newSkuPhotoFile) {
      showError('A product photo is required');
      return;
    }
    if (!context) {
      showError('Context notes are required');
      return;
    }
    setNewSkuConfirmSaving(true);
    try {
      const ext =
        (newSkuPhotoFile.name.split('.').pop() || 'jpg').toLowerCase().replace(/^\./, '') || 'jpg';
      const contentType = newSkuPhotoFile.type || 'image/jpeg';
      const { uploadUrl, s3Key } = await getInventoryAuditorImageUploadUrl(ext, contentType);
      await uploadInventoryAuditorImageToPresignedUrl(uploadUrl, newSkuPhotoFile, contentType);
      const res = await resolveInventoryAuditorUpc(newSkuConfirm.scannedCode, {
        autoCreateInCatalog: true,
        uomPackType: newSkuConfirm.uomPackType,
        size: newSkuConfirm.size,
        imageS3Key: s3Key,
        scanContext: context,
      });
      if (!res.found || res.inventoryItemId == null) {
        showError('Could not create this catalog SKU');
        return;
      }
      mergeResolvedItemIntoGrid(
        res.inventoryItemId,
        res.retailUpc ?? res.primaryUpc ?? newSkuConfirm.retailUpc,
        res.name ?? newSkuConfirm.name,
        res.imageUrl ?? newSkuPhotoPreview ?? newSkuConfirm.imageUrl,
        res.uomPackType ?? newSkuConfirm.uomPackType,
        res.size ?? newSkuConfirm.size,
        res.catalogOrigin ?? 'scanned',
        res.scanContext ?? context,
      );
      resetNewSkuConfirmForm();
    } catch (e: unknown) {
      showError(e instanceof Error ? e.message : 'Create failed');
    } finally {
      setNewSkuConfirmSaving(false);
    }
  }, [
    newSkuConfirm,
    newSkuPhotoFile,
    newSkuPhotoPreview,
    newSkuScanContext,
    mergeResolvedItemIntoGrid,
    resetNewSkuConfirmForm,
    showError,
  ]);

  const breadcrumbs = [
    { label: 'Home', href: '/app/dashboard' },
    { label: 'Locations', href: '/app/inventory-auditor/locations' },
    { label: 'Full store count' },
  ];

  return (
    <>
      <TitleBar breadcrumbs={breadcrumbs} title="Full store count" />
      <Box sx={{ p: 2, maxWidth: 1100 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Scan or enter a UPC and set counted quantity for each SKU. Quantities save automatically—the last reading for
          today is what counts. UPCs not in the catalog require confirmation and are added as Scanned (not PDI inventory).
        </Typography>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start', mb: 2 }}>
          <Autocomplete
            sx={{ minWidth: 320 }}
            options={locations}
            loading={locationsLoading}
            getOptionLabel={(o: { id: number; name?: string }) => o.name || `Location #${o.id}`}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            value={
              selectedLocation
                ? locations.find((l: { id: number }) => l.id === selectedLocation.id) ?? null
                : null
            }
            onChange={(_, v) => setSelectedLocation(v ? { id: v.id, name: v.name } : null)}
            renderInput={(params) => <TextField {...params} label="Location" size="small" />}
          />
          {selectedLocation ? (
            <Button
              component={RouterLink}
              to={`/app/inventory-auditor/locations/${selectedLocation.id}`}
              size="small"
              sx={{ mt: 0.5, textTransform: 'none' }}
            >
              Location details…
            </Button>
          ) : null}
        </Box>

        {selectedLocation ? (
          <>
            {dayRolloverBanner ? (
              <Alert severity="info" sx={{ mb: 2 }} onClose={() => setDayRolloverBanner(false)}>
                New count day for {formatIaCountDateLabel(countDateYmd ?? '', countCalendarTimeZone)} — list is
                empty. Scan or enter a UPC to start counting.
              </Alert>
            ) : null}
            {countDateYmd ? (
              <Typography variant="body2" sx={{ mb: 1.5 }} color="text.secondary">
                <strong>Count date:</strong> {formatIaCountDateLabel(countDateYmd, countCalendarTimeZone)} — Quantities
                save automatically for this calendar day.
              </Typography>
            ) : null}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 2 }}>
              <TextField
                size="small"
                label="Search UPC / name"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{ width: 260 }}
                slotProps={{
                  input: {
                    endAdornment:
                      search.length > 0 ? (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            aria-label="Clear search"
                            edge="end"
                            onClick={() => setSearch('')}
                          >
                            <ClearIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ) : undefined,
                  },
                }}
              />
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel id="ia-uom-label">UOM (new SKU)</InputLabel>
                <Select
                  labelId="ia-uom-label"
                  label="UOM (new SKU)"
                  value={pickUom}
                  onChange={(e) => setPickUom(String(e.target.value))}
                >
                  {INVENTORY_AUDITOR_UOM_PACK_TYPES.map((u) => (
                    <MenuItem key={u} value={u}>
                      {u}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel id="ia-size-label">Size (new SKU)</InputLabel>
                <Select
                  labelId="ia-size-label"
                  label="Size (new SKU)"
                  value={pickSize}
                  onChange={(e) => setPickSize(String(e.target.value))}
                >
                  {INVENTORY_AUDITOR_SIZES.map((s) => (
                    <MenuItem key={s} value={s}>
                      {s}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                size="small"
                label="Add SKU by UPC"
                value={manualUpc}
                onChange={(e) => setManualUpc(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    void handleAddUpc();
                  }
                }}
                sx={{ width: 200 }}
                slotProps={{
                  input: {
                    endAdornment:
                      manualUpc.length > 0 ? (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            aria-label="Clear UPC"
                            edge="end"
                            onClick={() => setManualUpc('')}
                          >
                            <ClearIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ) : undefined,
                  },
                }}
              />
              <Button variant="outlined" size="small" onClick={() => void handleAddUpc()} sx={{ textTransform: 'none' }}>
                Look up
              </Button>
              {savingItemIds.size > 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Saving…
                </Typography>
              ) : null}
            </Box>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 48 }} />
                    <TableCell>UPC</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Origin</TableCell>
                    <TableCell>UOM</TableCell>
                    <TableCell>Size</TableCell>
                    <TableCell align="right">Count</TableCell>
                    <TableCell align="center" sx={{ width: 44, p: 0 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tableLoading ? (
                    <TableRow>
                      <TableCell colSpan={8}>
                        <Typography variant="body2" color="text.secondary">
                          Loading…
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8}>
                        <Typography variant="body2" color="text.secondary">
                          No rows yet. Add a UPC to start counting.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((r) => {
                      const k = rowKey(r.inventoryItem.id);
                      const saved = lastSavedQty[k] ?? r.quantity;
                      const draft = draftQty[k] ?? saved;
                      const it = r.inventoryItem;
                      const isSaving = savingItemIds.has(it.id);
                      const isHighlighted = highlightItemId === it.id;
                      return (
                        <TableRow
                          key={k}
                          data-inventory-item-id={it.id}
                          sx={isHighlighted ? { bgcolor: 'action.selected' } : undefined}
                        >
                          <TableCell>
                            <Avatar variant="rounded" src={it.imageUrl || undefined} sx={{ width: 36, height: 36 }}>
                              <Inventory2Icon fontSize="small" />
                            </Avatar>
                          </TableCell>
                          <TableCell>{it.upc}</TableCell>
                          <TableCell>
                            <Typography variant="body2">{it.name?.trim() || '—'}</Typography>
                            {it.catalogOrigin === 'scanned' && it.scanContext?.trim() ? (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                {it.scanContext.trim()}
                              </Typography>
                            ) : null}
                          </TableCell>
                          <TableCell>{inventoryAuditorCatalogOriginLabel(it.catalogOrigin)}</TableCell>
                          <TableCell>{it.uomPackType ?? '—'}</TableCell>
                          <TableCell>{it.size ?? '—'}</TableCell>
                          <TableCell align="right" sx={{ maxWidth: 120 }}>
                            {isSaving ? (
                              <Typography variant="body2" color="text.secondary">
                                Saving…
                              </Typography>
                            ) : (
                              <TextField
                                size="small"
                                type="text"
                                inputMode="numeric"
                                value={draft === 0 ? '' : String(draft)}
                                placeholder="0"
                                onChange={(e) => changeDraft(r.inventoryItem.id, e.target.value)}
                                inputRef={(el) => {
                                  countInputRefs.current[k] = el;
                                }}
                                sx={{ width: 96 }}
                              />
                            )}
                          </TableCell>
                          <TableCell align="center" sx={{ py: 0.5, px: 0 }}>
                            <IconButton
                              size="small"
                              aria-label="Remove row"
                              disabled={isSaving}
                              onClick={() => setRemoveRowItemId(it.id)}
                              sx={{ color: 'error.main' }}
                            >
                              <ClearIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        ) : null}
      </Box>
      <Dialog open={variantDialogOpen} onClose={() => setVariantDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Choose product variant</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            This UPC matches more than one catalog row (different UOM or Size). Pick the one you are counting.
          </Typography>
          <List dense>
            {pendingVariants.map((v) => (
              <ListItemButton key={v.inventoryItemId} onClick={() => handlePickVariant(v)}>
                <ListItemText
                  primary={`${v.uomPackType} · ${v.size}`}
                  secondary={[v.name?.trim() || '—', v.primaryUpc].filter(Boolean).join(' · ')}
                />
              </ListItemButton>
            ))}
          </List>
        </DialogContent>
      </Dialog>
      <Dialog
        open={newSkuConfirm != null}
        onClose={() => {
          if (!newSkuConfirmSaving) resetNewSkuConfirmForm();
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Not in catalog</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This UPC is not in the Inventory Auditor catalog (PDI inventory). Add a photo and context notes, then
            confirm to create a <strong>Scanned</strong> SKU.
          </Typography>
          {newSkuConfirm ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                <Avatar
                  variant="rounded"
                  src={newSkuPhotoPreview || newSkuConfirm.imageUrl || undefined}
                  sx={{ width: 72, height: 72 }}
                >
                  <Inventory2Icon />
                </Avatar>
                <Box>
                  <Typography variant="subtitle1">{newSkuConfirm.name?.trim() || 'Scanned product'}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    UPC {newSkuConfirm.retailUpc}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {newSkuConfirm.uomPackType} · {newSkuConfirm.size}
                  </Typography>
                </Box>
              </Box>
              <Box>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  Product photo <Typography component="span" color="error">*</Typography>
                </Typography>
                <input
                  ref={newSkuPhotoInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp,image/*"
                  capture="environment"
                  hidden
                  onChange={(e) => {
                    const f = e.target.files?.[0] ?? null;
                    if (newSkuPhotoPreview) URL.revokeObjectURL(newSkuPhotoPreview);
                    setNewSkuPhotoFile(f);
                    setNewSkuPhotoPreview(f ? URL.createObjectURL(f) : null);
                  }}
                />
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => newSkuPhotoInputRef.current?.click()}
                  sx={{ textTransform: 'none' }}
                >
                  {newSkuPhotoFile ? 'Change photo' : 'Take / choose photo'}
                </Button>
                {newSkuPhotoFile ? (
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                    {newSkuPhotoFile.name}
                  </Typography>
                ) : (
                  <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                    Required
                  </Typography>
                )}
              </Box>
              <TextField
                label="Context notes"
                required
                multiline
                minRows={3}
                fullWidth
                value={newSkuScanContext}
                onChange={(e) => setNewSkuScanContext(e.target.value)}
                placeholder="Where found, pack notes, shelf location, etc."
                helperText="Saved on the catalog item for future reference"
              />
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button disabled={newSkuConfirmSaving} onClick={() => resetNewSkuConfirmForm()}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={
              newSkuConfirmSaving || !newSkuPhotoFile || newSkuScanContext.trim() === ''
            }
            onClick={() => void handleConfirmNewScannedSku()}
          >
            {newSkuConfirmSaving ? 'Creating…' : 'Add as scanned SKU'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={removeRowItemId != null} onClose={() => setRemoveRowItemId(null)}>
        <DialogTitle>Remove row?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Remove{' '}
            <strong>
              {removeRowItemId != null ? rows.find((r) => r.inventoryItem.id === removeRowItemId)?.inventoryItem.upc : ''}
            </strong>{' '}
            from this count? Saves zero and removes the row from this list.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoveRowItemId(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleConfirmRemoveCountRow}>
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default InventoryAuditorLocationSiteVisitPage;
